'use memo';

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { ProductLogoMediumCertifications } from '@/components/Logo';
import { Text } from '@/components/Typography';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { ledgerColumn } from '../-shared/-components/ledgerColumn';
import { LedgerContent } from '../-shared/-components/LedgerContent';
import { LedgerDataTable } from '../-shared/-components/LedgerDataTable';
import { isForbiddenError, LedgerForbiddenState } from '../-shared/-components/LedgerForbiddenState';
import { LedgerHeader } from '../-shared/-components/LedgerHeader';
import { Field, FieldGrid, ResourceDetailCard } from '../-shared/-components/ResourceDetailCard';
import { useCertificationLedger } from '../-shared/-query';
import { formatDate } from '../-shared/-utils';
const CERTIFICATIONS_PATH = '/individual/portal/lookup/certifications';
const LOOKUP_PATH = '/individual/portal/lookup';
const certificationsSearchSchema = z.object({
  id: z.string().optional(),
  pageIndex: z.coerce.number().int().min(0).catch(0),
  pageSize: z.coerce.number().int().min(1).max(100).catch(20)
});
export const Route = createFileRoute('/_individual/individual/portal/lookup/_ledger/certifications/')({
  validateSearch: certificationsSearchSchema,
  component: CertificationsLookupPage
});
type CertificationLedgerItem = NonNullable<ReturnType<typeof useCertificationLedger>['data']>['items'][number];
function CertificationsLookupPage() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.lookupResources'
  });
  const {
    t: ct
  } = useTranslation(defaultNS, {
    keyPrefix: 'common.breadcrumbs'
  });
  const {
    t: th
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.home'
  });
  const {
    id,
    pageIndex,
    pageSize
  } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [freeText, setFreeText] = useState('');
  const debouncedSetFreeText = useDebouncedCallback((text: string) => {
    setFreeText(text);
    // 検索語変更時は 1 ページ目に戻す（存在しないページで空表示になるのを防ぐ）。
    void navigate({
      search: prev => ({
        ...prev,
        pageIndex: 0
      })
    });
  }, []);
  const {
    data,
    isPending,
    isError,
    error
  } = useCertificationLedger({
    page: pageIndex + 1,
    pageSize,
    freeText: freeText === '' ? undefined : freeText
  });
  const selected = id != null ? data?.items.find(c => c.id === id) ?? null : null;
  if (isError && isForbiddenError(error)) {
    return <>
        <LedgerHeader breadcrumbs={[{
        label: ct('home'),
        link: {
          to: '/'
        }
      }, {
        label: th('lookup.title'),
        link: {
          to: LOOKUP_PATH
        }
      }, {
        label: t('certifications.title')
      }]} Logo={ProductLogoMediumCertifications} title={t('certifications.title')} />
        <LedgerContent>
          <LedgerForbiddenState onBack={() => void navigate({
          to: LOOKUP_PATH
        })} />
        </LedgerContent>
      </>;
  }
  if (isError) {
    throw error;
  }
  if (selected != null) {
    return <>
        <LedgerHeader breadcrumbs={[{
        label: ct('home'),
        link: {
          to: '/'
        }
      }, {
        label: th('lookup.title'),
        link: {
          to: LOOKUP_PATH
        }
      }, {
        label: t('certifications.title'),
        link: {
          to: CERTIFICATIONS_PATH,
          search: {
            pageIndex,
            pageSize
          }
        }
      }, {
        label: selected.certificationName
      }]} Logo={ProductLogoMediumCertifications} title={selected.certificationName} subtitle={selected.certificationNumber ?? undefined} />
        <LedgerContent>
          <ResourceDetailCard title={t('certifications.detail.basicInfo')}>
            <FieldGrid>
              <Field label={t('certifications.detail.certificationKind')} value={selected.certificationKindLabel} />
              <Field label={t('certifications.detail.certificationNumber')} value={selected.certificationNumber ?? '—'} />
              <Field label={t('certifications.detail.lastRenewalDate')} value={formatDate(selected.lastRenewalDate)} />
              <Field label={t('certifications.detail.validTo')} value={formatDate(selected.validTo)} />
              <Field label={t('certifications.detail.renewalDeadline')} value={formatDate(selected.renewalDeadline)} />
            </FieldGrid>
          </ResourceDetailCard>
        </LedgerContent>
      </>;
  }
  const columns = [ledgerColumn<CertificationLedgerItem>({
    id: 'certificationName',
    header: t('certifications.headers.name'),
    cell: c => <Text variant="label">{c.certificationName}</Text>
  }), ledgerColumn<CertificationLedgerItem>({
    id: 'certificationKind',
    header: t('certifications.headers.certificationKind'),
    cell: c => c.certificationKindLabel
  }), ledgerColumn<CertificationLedgerItem>({
    id: 'certificationNumber',
    header: t('certifications.headers.number'),
    cell: c => c.certificationNumber ?? '—'
  }), ledgerColumn<CertificationLedgerItem>({
    id: 'lastRenewalDate',
    header: t('certifications.headers.lastUpdated'),
    cell: c => formatDate(c.lastRenewalDate)
  }), ledgerColumn<CertificationLedgerItem>({
    id: 'validTo',
    header: t('certifications.headers.expireAt'),
    cell: c => formatDate(c.validTo)
  }), ledgerColumn<CertificationLedgerItem>({
    id: 'renewalDeadline',
    header: t('certifications.headers.renewBy'),
    cell: c => formatDate(c.renewalDeadline)
  })];
  return <>
      <LedgerHeader breadcrumbs={[{
      label: ct('home'),
      link: {
        to: '/'
      }
    }, {
      label: th('lookup.title'),
      link: {
        to: LOOKUP_PATH
      }
    }, {
      label: t('certifications.title')
    }]} Logo={ProductLogoMediumCertifications} title={t('certifications.title')} />
      <LedgerContent className="h-full">
        <LedgerDataTable<CertificationLedgerItem> fillHeight manualPagination columns={columns} data={data?.items} isPending={isPending} pageCount={data?.maxPage ?? 0} totalCount={data?.count} pagination={{
        pageIndex,
        pageSize
      }} onPaginationChange={updater => {
        const next = typeof updater === 'function' ? updater({
          pageIndex,
          pageSize
        }) : updater;
        void navigate({
          search: prev => ({
            ...prev,
            pageIndex: next.pageIndex,
            pageSize: next.pageSize
          })
        });
      }} onSearchTextChange={debouncedSetFreeText} getRowId={c => c.id} searchPlaceholder={t('common.search')} onRowSelect={c => void navigate({
        search: prev => ({
          ...prev,
          id: c.id
        })
      })} emptyContent={<Text variant="p" className="text-neutral-muted-on-fill">
              {t('common.empty')}
            </Text>} />
      </LedgerContent>
    </>;
}